/**
 * The admin's icon vocabulary, backed by lucide (the set shadcn uses).
 *
 * Call sites name icons by intent, not by lucide's spelling, so swapping a glyph
 * is one line here rather than a find-and-replace. Stroke weight is set once in
 * globals.css (`svg.lucide`) — lucide's default of 2 reads heavy at 14–18px.
 */

import {
  Archive,
  Award,
  Bell,
  Building2,
  Calendar,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronsUpDown,
  CircleDot,
  Database,
  FileText,
  Flag,
  Folder,
  FolderOpen,
  ClipboardList,
  Gavel,
  GitFork,
  Globe,
  GripVertical,
  Handshake,
  House,
  Layers,
  LayoutGrid,
  ListOrdered,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  Milestone,
  MoreVertical,
  Plus,
  Route,
  Search,
  Settings,
  SlidersHorizontal,
  Swords,
  Tag,
  Target,
  Trophy,
  User,
  Users,
  Waypoints,
  Waves,
  X,
  Zap,
} from "lucide-react";

type IconProps = { size?: number };

const D = 18;

export const IconGrid = ({ size = D }: IconProps) => <LayoutGrid size={size} />;
export const IconHome = ({ size = D }: IconProps) => <House size={size} />;
export const IconUsers = ({ size = D }: IconProps) => <Users size={size} />;
export const IconUser = ({ size = D }: IconProps) => <User size={size} />;
export const IconTrophy = ({ size = D }: IconProps) => <Trophy size={size} />;
export const IconMapPin = ({ size = D }: IconProps) => <MapPin size={size} />;
export const IconTag = ({ size = D }: IconProps) => <Tag size={size} />;
export const IconCalendar = ({ size = D }: IconProps) => <Calendar size={size} />;
export const IconFileText = ({ size = D }: IconProps) => <FileText size={size} />;
export const IconDatabase = ({ size = D }: IconProps) => <Database size={size} />;
export const IconPlus = ({ size = D }: IconProps) => <Plus size={size} />;
export const IconSettings = ({ size = D }: IconProps) => <Settings size={size} />;
export const IconLayers = ({ size = D }: IconProps) => <Layers size={size} />;
export const IconBracket = ({ size = D }: IconProps) => <GitFork size={size} />;
export const IconZap = ({ size = D }: IconProps) => <Zap size={size} />;
export const IconSwords = ({ size = D }: IconProps) => <Swords size={size} />;
export const IconFlag = ({ size = D }: IconProps) => <Flag size={size} />;
export const IconRoute = ({ size = D }: IconProps) => <Route size={size} />;
export const IconTarget = ({ size = D }: IconProps) => <Target size={size} />;
export const IconCircleDot = ({ size = D }: IconProps) => <CircleDot size={size} />;
export const IconAward = ({ size = D }: IconProps) => <Award size={size} />;
export const IconList = ({ size = D }: IconProps) => <ListOrdered size={size} />;
export const IconSearch = ({ size = D }: IconProps) => <Search size={size} />;
export const IconSliders = ({ size = D }: IconProps) => <SlidersHorizontal size={size} />;
export const IconX = ({ size = D }: IconProps) => <X size={size} />;
export const IconCheck = ({ size = D }: IconProps) => <Check size={size} />;
export const IconBell = ({ size = D }: IconProps) => <Bell size={size} />;
export const IconChevronDown = ({ size = D }: IconProps) => <ChevronDown size={size} />;
export const IconSwitch = ({ size = D }: IconProps) => <ChevronsUpDown size={size} />;
export const IconGrip = ({ size = D }: IconProps) => <GripVertical size={size} />;
export const IconMore = ({ size = D }: IconProps) => <MoreVertical size={size} />;
export const IconSignOut = ({ size = D }: IconProps) => <LogOut size={size} />;
export const IconWaves = ({ size = D }: IconProps) => <Waves size={size} />;
export const IconClub = ({ size = D }: IconProps) => <Building2 size={size} />;
export const IconSeason = ({ size = D }: IconProps) => <CalendarDays size={size} />;
export const IconStage = ({ size = D }: IconProps) => <Milestone size={size} />;
export const IconOfficial = ({ size = D }: IconProps) => <Gavel size={size} />;
export const IconCoach = ({ size = D }: IconProps) => <ClipboardList size={size} />;
export const IconDeal = ({ size = D }: IconProps) => <Handshake size={size} />;
export const IconNetwork = ({ size = D }: IconProps) => <Waypoints size={size} />;
export const IconChat = ({ size = D }: IconProps) => <MessageSquare size={size} />;
export const IconMail = ({ size = D }: IconProps) => <Mail size={size} />;
export const IconChart = ({ size = D }: IconProps) => <ChartColumn size={size} />;
export const IconArchive = ({ size = D }: IconProps) => <Archive size={size} />;
export const IconGlobe = ({ size = D }: IconProps) => <Globe size={size} />;
export const IconFolder = ({ size = D }: IconProps) => <Folder size={size} />;

/** The twist on a tree row. A filled triangle rather than a chevron: at 11px a
 *  stroked chevron is three grey pixels, and the whole job of this control is to be
 *  found. */
export function IconTriangle({ size = D }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
      <path d="M3 1.5 7.5 5 3 8.5z" />
    </svg>
  );
}
export const IconFolderOpen = ({ size = D }: IconProps) => <FolderOpen size={size} />;

/** A hoofprint. Kept hand-drawn: lucide has no horse, and the nearest animal
 * glyph would be a lie on a racecard. */
export function IconHoof({ size = D }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8.6 3.8c2 0 3.4 2.3 3.4 5.6S10.6 15 8.6 15 5.2 12.7 5.2 9.4 6.6 3.8 8.6 3.8Z" />
      <path d="M17 8.6c1.6 0 2.8 1.9 2.8 4.6s-1.2 4.6-2.8 4.6-2.8-1.9-2.8-4.6S15.4 8.6 17 8.6Z" />
    </svg>
  );
}

/** Named lookup, so config (a structure template, a master) can carry an icon
 * without importing components. */
export const ICON_BY_NAME = {
  layers: IconLayers,
  bracket: IconBracket,
  calendar: IconCalendar,
  zap: IconZap,
  swords: IconSwords,
  flag: IconFlag,
  route: IconRoute,
  target: IconTarget,
  circleDot: IconCircleDot,
  trophy: IconTrophy,
  award: IconAward,
  user: IconUser,
  users: IconUsers,
  pin: IconMapPin,
  hoof: IconHoof,
  tag: IconTag,
  plus: IconPlus,
  list: IconList,
  sliders: IconSliders,
  search: IconSearch,
  waves: IconWaves,
  home: IconHome,
  club: IconClub,
  season: IconSeason,
  stage: IconStage,
  official: IconOfficial,
  coach: IconCoach,
  deal: IconDeal,
  network: IconNetwork,
  chat: IconChat,
  mail: IconMail,
  chart: IconChart,
  archive: IconArchive,
  globe: IconGlobe,
  grid: IconGrid,
  fileText: IconFileText,
  database: IconDatabase,
  settings: IconSettings,
} as const;

export type IconName = keyof typeof ICON_BY_NAME;

export function Icon({ name, size = D }: IconProps & { name: IconName }) {
  const Glyph = ICON_BY_NAME[name];
  return <Glyph size={size} />;
}
